/**
 * Axios-клиент с Bearer-интерсептором + 401-refresh.
 *
 * Использование: `import { api } from '../requests/api'; api.get('/public/general?kato=...')`.
 * Если пользователь не аутентифицирован — запрос уходит без Bearer; 401 на
 * закрытом эндпоинте триггерит refresh-token и retry.
 *
 * Открытость `/api/public/*` зависит от стенда: при `PUBLIC_READ_REQUIRES_AUTH`
 * бэкенд требует токен и там (DP-348). Фронту знать про флаг не нужно — он
 * отправляет Bearer всегда, когда пользователь залогинен, а на 401 у анонима
 * уводит на логин.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { env } from '../consts/env';
import keycloak from '../keycloak/keycloak';

/** Сколько секунд до истечения токена — пересоздаём через refresh. */
const TOKEN_MIN_VALIDITY_SECONDS = 30;

export const api = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 10000,
  headers: { Accept: 'application/json' },
});

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  if (keycloak.authenticated) {
    try {
      await keycloak.updateToken(TOKEN_MIN_VALIDITY_SECONDS);
    } catch {
      // Refresh-token истёк — пользователю надо залогиниться заново.
      await keycloak.login();
      // login() редиректит — этот return формальный.
      return config;
    }
    config.headers.Authorization = `Bearer ${keycloak.token ?? ''}`;
  }
  return config;
});

interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;

    if (error.response?.status === 401 && original && !original._retry && keycloak.authenticated) {
      original._retry = true;
      try {
        // Принудительный refresh (любой возраст токена).
        await keycloak.updateToken(-1);
        original.headers.Authorization = `Bearer ${keycloak.token ?? ''}`;
        return api.request(original);
      } catch {
        await keycloak.login();
        return Promise.reject(error);
      }
    }

    // 401 у неаутентифицированного — стенд с PUBLIC_READ_REQUIRES_AUTH=true:
    // там `/api/public/*` больше не открыты. Без явного логина страница просто
    // осталась бы пустой, и это выглядело бы как «данные не пришли».
    if (error.response?.status === 401 && !keycloak.authenticated) {
      await keycloak.login();
    }
    return Promise.reject(error);
  },
);
